import {
  ActionIcon,
  Alert,
  Autocomplete,
  Badge,
  Button,
  FileButton,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconMicrophone,
  IconPlus,
  IconRss,
  IconStack2,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useState } from "react";
import { describeRpcError, usePermission } from "@how/app-kit";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  RecordDrawer,
  StatusBadge,
  useReturnFocus,
  useSearchParamState,
} from "@how/ui";
import {
  formatDuration,
  formatSermonDate,
  richTextFromMarkup,
  richTextToMarkup,
  sermonPaths,
} from "@how/website-core";
import { useCampuses } from "../campuses/campus-queries";
import { mediaUrl } from "./media-queries";
import {
  audioFileProblem,
  PODCAST_CATEGORIES,
  sermonProblems,
  useDeleteSeries,
  useDeleteSermon,
  useDeleteSpeaker,
  usePublishSermon,
  useSaveSermon,
  useSaveSermonSettings,
  useSaveSeries,
  useSaveSpeaker,
  useSermons,
  useSermonSeries,
  useSermonSettings,
  useSermonSpeakers,
  useUploadSermonAudio,
  type ResourceLink,
  type SeriesRow,
  type SermonDraft,
  type SermonRow,
  type SermonSettingsRow,
  type SpeakerRow,
} from "./sermon-queries";
import { useAssets, usePublicHost } from "./website-queries";
import { WebsiteTabs } from "./WebsiteTabs";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? "yellow" : "red",
    title,
    message: described.message,
  });
}

const today = (): string => new Date().toISOString().slice(0, 10);

/**
 * Website → Sermons (ADR 0061): the church's sermon library, as Planning Center Publishing's episodes. Staff with
 * sermons.manage add a sermon with its date, speaker, series, scripture, video, audio, notes and links, publish it to
 * the website, the podcast and the member portal, and keep the series, speakers and podcast here too.
 */
export function SermonsPage() {
  const canManage = usePermission("sermons.manage", { module: "sermons_media", write: true });
  const sermons = useSermons();
  const series = useSermonSeries();
  const speakers = useSermonSpeakers();
  const host = usePublicHost();
  const publish = usePublishSermon();
  const remove = useDeleteSermon();
  const [search, setSearch] = useSearchParamState("q");
  const [seriesFilter, setSeriesFilter] = useSearchParamState("series");
  const [speakerFilter, setSpeakerFilter] = useSearchParamState("speaker");
  const [fromFilter, setFromFilter] = useSearchParamState("from");
  const [untilFilter, setUntilFilter] = useSearchParamState("until");
  const [editing, setEditing] = useState<SermonRow | "new" | null>(null);
  const [deleting, setDeleting] = useState<SermonRow | null>(null);
  const [panel, setPanel] = useState<"series" | "speakers" | "podcast" | null>(null);
  const editorFocus = useReturnFocus();
  const panelFocus = useReturnFocus();
  const editable = canManage.data === true;

  const seriesById = new Map((series.data ?? []).map((s) => [s.id, s]));
  const speakerById = new Map((speakers.data ?? []).map((s) => [s.id, s]));
  const needle = search.trim().toLowerCase();
  const listed = (sermons.data ?? []).filter(
    (s) =>
      (seriesFilter === "" || s.seriesId === seriesFilter) &&
      (speakerFilter === "" || s.speakerId === speakerFilter) &&
      (fromFilter === "" || s.preachedOn >= fromFilter) &&
      (untilFilter === "" || s.preachedOn <= untilFilter) &&
      (needle === "" ||
        s.title.toLowerCase().includes(needle) ||
        s.scripture.some((r) => r.toLowerCase().includes(needle))),
  );
  const siteLink = (path: string) =>
    host.data === null || host.data === undefined ? null : `https://${host.data}${path}`;

  const openEditor = (target: SermonRow | "new") => {
    editorFocus.remember();
    setEditing(target);
  };
  const openPanel = (which: "series" | "speakers" | "podcast") => {
    panelFocus.remember();
    setPanel(which);
  };

  return (
    <>
      <PageHeader
        title="Website"
        description="Sermons on your website, in your podcast and in the member portal."
        actions={
          editable ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                openEditor("new");
              }}
            >
              Add sermon
            </Button>
          ) : null
        }
      />
      <WebsiteTabs active="sermons" />
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value === "" ? undefined : value);
        }}
        searchPlaceholder="Search by title or scripture"
        filters={
          <>
            <Select
              aria-label="Series"
              placeholder="Every series"
              clearable
              data={(series.data ?? []).map((s) => ({ value: s.id, label: s.title }))}
              value={seriesFilter === "" ? null : seriesFilter}
              onChange={(value) => {
                setSeriesFilter(value ?? undefined);
              }}
            />
            <Select
              aria-label="Speaker"
              placeholder="Every speaker"
              clearable
              data={(speakers.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              value={speakerFilter === "" ? null : speakerFilter}
              onChange={(value) => {
                setSpeakerFilter(value ?? undefined);
              }}
            />
            <TextInput
              type="date"
              aria-label="Preached from"
              value={fromFilter}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFromFilter(value === "" ? undefined : value);
              }}
            />
            <TextInput
              type="date"
              aria-label="Preached until"
              value={untilFilter}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setUntilFilter(value === "" ? undefined : value);
              }}
            />
          </>
        }
        actions={
          editable ? (
            <Group gap="xs" wrap="nowrap">
              <Button
                variant="default"
                leftSection={<IconStack2 size={16} />}
                style={{ flexShrink: 0 }}
                onClick={() => {
                  openPanel("series");
                }}
              >
                Series
              </Button>
              <Button
                variant="default"
                leftSection={<IconMicrophone size={16} />}
                style={{ flexShrink: 0 }}
                onClick={() => {
                  openPanel("speakers");
                }}
              >
                Speakers
              </Button>
              <Button
                variant="default"
                leftSection={<IconRss size={16} />}
                style={{ flexShrink: 0 }}
                onClick={() => {
                  openPanel("podcast");
                }}
              >
                Podcast
              </Button>
            </Group>
          ) : null
        }
      />
      {sermons.isPending ? (
        <LoadingState rows={5} />
      ) : sermons.isError ? (
        <ErrorState message={sermons.error.message} onRetry={() => void sermons.refetch()} />
      ) : sermons.data.length === 0 ? (
        <EmptyState
          title="No sermons yet"
          description="Add your first sermon with its video or audio and notes. Publish it and it appears on your website at /sermons, in the member portal, and in your podcast."
        />
      ) : listed.length === 0 ? (
        <EmptyState title="No sermons match" description="Try another series, speaker or date." />
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Sermon</Table.Th>
                <Table.Th>Preached</Table.Th>
                <Table.Th>Speaker</Table.Th>
                <Table.Th>Series</Table.Th>
                <Table.Th>Media</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th aria-label="Actions" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {listed.map((s) => {
                const link = siteLink(sermonPaths.sermon(s.slug));
                return (
                  <Table.Tr key={s.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {s.title}
                      </Text>
                      {s.scripture.length === 0 ? null : (
                        <Text size="xs" c="dimmed">
                          {s.scripture.join("; ")}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{formatSermonDate(s.preachedOn)}</Table.Td>
                    <Table.Td>
                      {s.speakerId === null ? "" : (speakerById.get(s.speakerId)?.name ?? "")}
                    </Table.Td>
                    <Table.Td>
                      {s.seriesId === null ? "" : (seriesById.get(s.seriesId)?.title ?? "")}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {s.videoUrl === null ? null : (
                          <Badge size="sm" variant="light" color="gray">
                            Video
                          </Badge>
                        )}
                        {s.audioPath === null && s.audioUrl === null ? null : (
                          <Badge size="sm" variant="light" color="gray">
                            Audio
                          </Badge>
                        )}
                        {s.notes === null ? null : (
                          <Badge size="sm" variant="light" color="gray">
                            Notes
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge
                        domain="page_status"
                        value={s.isPublished ? "published" : "draft"}
                        label={s.isPublished ? "Published" : "Draft"}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        {s.isPublished && link !== null ? (
                          <Button
                            component="a"
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            size="compact-xs"
                            variant="subtle"
                            aria-label={`View ${s.title} on the website (opens in a new tab)`}
                          >
                            View
                          </Button>
                        ) : null}
                        {editable ? (
                          <>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              aria-label={`Edit ${s.title}`}
                              onClick={() => {
                                openEditor(s);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              aria-label={`${s.isPublished ? "Unpublish" : "Publish"} ${s.title}`}
                              onClick={() => {
                                publish.mutate(
                                  { sermonId: s.id, published: !s.isPublished },
                                  {
                                    onSuccess: () => {
                                      notifications.show({
                                        color: "teal",
                                        title: s.isPublished ? "Taken off the site" : "Published",
                                        message: s.title,
                                      });
                                    },
                                    onError: (error) => {
                                      notifyError("Not changed", error);
                                    },
                                  },
                                );
                              }}
                            >
                              {s.isPublished ? "Unpublish" : "Publish"}
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              color="red"
                              aria-label={`Delete ${s.title}`}
                              onClick={() => {
                                setDeleting(s);
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {editing === null ? null : (
        <SermonDrawer
          sermon={editing === "new" ? null : editing}
          series={series.data ?? []}
          speakers={speakers.data ?? []}
          onClose={() => {
            setEditing(null);
            editorFocus.restore();
          }}
        />
      )}
      {panel === "series" ? (
        <SeriesModal
          series={series.data ?? []}
          onClose={() => {
            setPanel(null);
            panelFocus.restore();
          }}
        />
      ) : panel === "speakers" ? (
        <SpeakersModal
          speakers={speakers.data ?? []}
          onClose={() => {
            setPanel(null);
            panelFocus.restore();
          }}
        />
      ) : panel === "podcast" ? (
        <PodcastModal
          feedUrl={siteLink(sermonPaths.feed)}
          onClose={() => {
            setPanel(null);
            panelFocus.restore();
          }}
        />
      ) : null}
      <ConfirmActionModal
        opened={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title="Delete this sermon"
        message={`Delete ${deleting?.title ?? ""}? It leaves the website, the podcast and the member portal at once, and its uploaded audio is removed. This cannot be undone.`}
        confirmLabel="Delete sermon"
        danger
        loading={remove.isPending}
        onConfirm={async () => {
          if (deleting === null) return;
          try {
            await remove.mutateAsync(deleting.id);
            notifications.show({ color: "teal", title: "Sermon deleted", message: deleting.title });
          } catch (error) {
            notifyError("Not deleted", error);
          }
          setDeleting(null);
        }}
      />
    </>
  );
}

// --------------------------------------------------------------------------------------------------- the sermon

function draftOf(sermon: SermonRow | null, speakers: readonly SpeakerRow[]): SermonDraft {
  if (sermon === null) {
    return {
      title: "",
      preachedOn: today(),
      speakerId: null,
      speakerName: "",
      seriesId: null,
      campusId: null,
      scripture: [],
      summary: "",
      videoUrl: "",
      audio: { kind: "none" },
      notes: null,
      resources: [],
      artworkPath: null,
      artworkAlt: null,
    };
  }
  return {
    title: sermon.title,
    preachedOn: sermon.preachedOn,
    speakerId: sermon.speakerId,
    speakerName: speakers.find((s) => s.id === sermon.speakerId)?.name ?? "",
    seriesId: sermon.seriesId,
    campusId: sermon.campusId,
    scripture: sermon.scripture,
    summary: sermon.summary ?? "",
    videoUrl: sermon.videoUrl ?? "",
    audio:
      sermon.audioPath !== null
        ? {
            kind: "file",
            path: sermon.audioPath,
            mime: sermon.audioMime ?? "audio/mpeg",
            bytes: sermon.audioBytes ?? 0,
            durationSeconds: sermon.audioDurationSeconds,
          }
        : sermon.audioUrl !== null
          ? { kind: "link", url: sermon.audioUrl }
          : { kind: "none" },
    notes: sermon.notes,
    resources: sermon.resources,
    artworkPath: sermon.artworkPath,
    artworkAlt: sermon.artworkAlt,
  };
}

function SermonDrawer({
  sermon,
  series,
  speakers,
  onClose,
}: {
  readonly sermon: SermonRow | null;
  readonly series: readonly SeriesRow[];
  readonly speakers: readonly SpeakerRow[];
  readonly onClose: () => void;
}) {
  const save = useSaveSermon();
  const upload = useUploadSermonAudio();
  const campuses = useCampuses();
  const assets = useAssets();
  const [draft, setDraft] = useState<SermonDraft>(() => draftOf(sermon, speakers));
  const [notesText, setNotesText] = useState(() =>
    sermon?.notes === null || sermon?.notes === undefined ? "" : richTextToMarkup(sermon.notes),
  );
  const [audioMode, setAudioMode] = useState<"file" | "link">(
    draft.audio.kind === "link" ? "link" : "file",
  );
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problems = sermonProblems(draft);
  const shown = (key: string) => (touched ? problems[key] : undefined);
  const set = (patch: Partial<SermonDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };
  const activeSpeakers = speakers.filter((s) => s.isActive || s.id === draft.speakerId);
  const activeCampuses = (campuses.data ?? []).filter((c) => c.isActive || c.id === draft.campusId);

  const submit = async () => {
    setTouched(true);
    setError(null);
    if (Object.keys(problems).length > 0) return;
    try {
      await save.mutateAsync({ sermonId: sermon?.id ?? null, draft });
      notifications.show({
        color: "teal",
        title: sermon === null ? "Sermon added" : "Sermon saved",
        message:
          sermon?.isPublished === true
            ? draft.title
            : `${draft.title} is a draft. Publish it from the list when it is ready.`,
      });
      onClose();
    } catch (e) {
      setError(describeRpcError(e).message);
    }
  };

  return (
    <RecordDrawer
      opened
      onClose={onClose}
      title={sermon === null ? "Add sermon" : "Edit sermon"}
      closeButtonProps={{ "aria-label": "Close" }}
    >
      <Stack gap="md">
        <TextInput
          label="Title"
          required
          maxLength={160}
          value={draft.title}
          error={shown("title")}
          onChange={(event) => {
            set({ title: event.currentTarget.value });
          }}
        />
        <Group grow align="flex-start">
          <TextInput
            type="date"
            label="Preached on"
            required
            value={draft.preachedOn}
            error={shown("preachedOn")}
            onChange={(event) => {
              set({ preachedOn: event.currentTarget.value });
            }}
          />
          <Autocomplete
            label="Speaker"
            description="Choose from your speakers, or type a new name."
            data={activeSpeakers.map((s) => s.name)}
            value={draft.speakerName}
            onChange={(value) => {
              const match = speakers.find(
                (s) => s.name.toLowerCase() === value.trim().toLowerCase(),
              );
              set({ speakerName: value, speakerId: match?.id ?? null });
            }}
          />
        </Group>
        <Group grow align="flex-start">
          <Select
            label="Series"
            placeholder="No series"
            clearable
            data={series.map((s) => ({ value: s.id, label: s.title }))}
            value={draft.seriesId}
            onChange={(value) => {
              set({ seriesId: value });
            }}
          />
          {activeCampuses.length === 0 ? null : (
            <Select
              label="Campus"
              placeholder="Every campus"
              clearable
              data={activeCampuses.map((c) => ({ value: c.id, label: c.name }))}
              value={draft.campusId}
              onChange={(value) => {
                set({ campusId: value });
              }}
            />
          )}
        </Group>
        <TagsInput
          label="Scripture"
          description="Press Enter after each reference, such as John 3:16-21."
          maxTags={10}
          value={[...draft.scripture]}
          error={shown("scripture")}
          onChange={(value) => {
            set({ scripture: value });
          }}
        />
        <Textarea
          label="Summary"
          description="A sentence or two. It shows on the website, in the portal and in podcast apps."
          autosize
          minRows={2}
          maxLength={2000}
          value={draft.summary}
          error={shown("summary")}
          onChange={(event) => {
            set({ summary: event.currentTarget.value });
          }}
        />
        <Title order={2} size="h5">
          Media
        </Title>
        <TextInput
          label="Video"
          description="A YouTube or Vimeo video's address. Your website plays it in the page."
          placeholder="https://www.youtube.com/watch?v=…"
          value={draft.videoUrl}
          error={shown("videoUrl")}
          onChange={(event) => {
            set({ videoUrl: event.currentTarget.value });
          }}
        />
        <Stack gap={6}>
          <Text size="sm" fw={500}>
            Audio
          </Text>
          <SegmentedControl
            aria-label="Audio"
            data={[
              { value: "file", label: "Upload a file" },
              { value: "link", label: "Link to a file" },
            ]}
            value={audioMode}
            onChange={(value) => {
              const mode = value === "link" ? "link" : "file";
              setAudioMode(mode);
              if (mode === "link" && draft.audio.kind !== "link")
                set({ audio: { kind: "link", url: "" } });
              if (mode === "file" && draft.audio.kind === "link") set({ audio: { kind: "none" } });
            }}
          />
          {audioMode === "file" ? (
            <Group gap="sm">
              <FileButton
                accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,.mp3,.m4a,.aac"
                onChange={(file) => {
                  if (file === null) return;
                  const problem = audioFileProblem(file);
                  if (problem !== null) {
                    notifications.show({ color: "red", title: "Not uploaded", message: problem });
                    return;
                  }
                  upload.mutate(file, {
                    onSuccess: (done) => {
                      set({ audio: { kind: "file", ...done } });
                      notifications.show({
                        color: "teal",
                        title: "Audio uploaded",
                        message: file.name,
                      });
                    },
                    onError: (e) => {
                      notifyError("Not uploaded", e);
                    },
                  });
                }}
              >
                {(props) => (
                  <Button
                    {...props}
                    variant="default"
                    loading={upload.isPending}
                    leftSection={<IconUpload size={16} />}
                  >
                    {draft.audio.kind === "file" ? "Replace the audio" : "Choose an audio file"}
                  </Button>
                )}
              </FileButton>
              <Text size="sm" c="dimmed">
                {draft.audio.kind === "file"
                  ? `Uploaded${
                      draft.audio.durationSeconds === null
                        ? ""
                        : `, ${formatDuration(draft.audio.durationSeconds)}`
                    }, ${String(Math.round(draft.audio.bytes / 100_000) / 10)} MB`
                  : "MP3, AAC or M4A, up to 50 MB"}
              </Text>
              {draft.audio.kind === "file" ? (
                <Button
                  size="compact-sm"
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    set({ audio: { kind: "none" } });
                  }}
                >
                  Remove the audio
                </Button>
              ) : null}
            </Group>
          ) : (
            <TextInput
              aria-label="Audio link"
              description="Where the file is hosted, starting https://. Podcast apps play it from there."
              placeholder="https://"
              value={draft.audio.kind === "link" ? draft.audio.url : ""}
              error={shown("audioUrl")}
              onChange={(event) => {
                set({ audio: { kind: "link", url: event.currentTarget.value } });
              }}
            />
          )}
        </Stack>
        <Select
          label="Artwork"
          description="From your images. Without one, the series's artwork is used."
          placeholder="The series's artwork"
          clearable
          disabled={assets.isError}
          data={(assets.data ?? []).map((a) => ({ value: a.storage_path, label: a.alt_text }))}
          value={draft.artworkPath}
          onChange={(value) => {
            const asset = (assets.data ?? []).find((a) => a.storage_path === value);
            set({ artworkPath: value, artworkAlt: asset?.alt_text ?? null });
          }}
        />
        {draft.artworkPath === null ? null : (
          <img
            src={mediaUrl(draft.artworkPath)}
            alt=""
            style={{ maxHeight: 120, borderRadius: 8, alignSelf: "flex-start" }}
          />
        )}
        <Title order={2} size="h5">
          Notes and links
        </Title>
        <Textarea
          label="Notes"
          description="An outline people can follow along with. Blank line between paragraphs. ## heading, - list item, 1. numbered item, > quote, **bold**, *italic*, [link text](https://…)."
          autosize
          minRows={5}
          value={notesText}
          onChange={(event) => {
            const text = event.currentTarget.value;
            setNotesText(text);
            const nodes = richTextFromMarkup(text);
            set({ notes: nodes.length === 0 ? null : nodes });
          }}
        />
        <ResourcesEditor
          value={draft.resources}
          error={shown("resources")}
          onChange={(resources) => {
            set({ resources });
          }}
        />
        {error === null ? null : (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={upload.isPending}
            onClick={() => void submit()}
          >
            {sermon === null ? "Add sermon" : "Save sermon"}
          </Button>
        </Group>
      </Stack>
    </RecordDrawer>
  );
}

function ResourcesEditor({
  value,
  error,
  onChange,
}: {
  readonly value: readonly ResourceLink[];
  readonly error: string | undefined;
  readonly onChange: (next: readonly ResourceLink[]) => void;
}) {
  const update = (index: number, patch: Partial<ResourceLink>) => {
    onChange(value.map((link, i) => (i === index ? { ...link, ...patch } : link)));
  };
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        Links
      </Text>
      <Text size="xs" c="dimmed">
        A study guide, a slide deck, a sign-up: anything that goes with this sermon.
      </Text>
      {value.map((link, index) => {
        const name = link.label.trim() === "" ? `link ${String(index + 1)}` : link.label.trim();
        return (
          <Group key={index} gap="xs" align="flex-end" wrap="nowrap">
            <TextInput
              label="Label"
              aria-label={`Label of link ${String(index + 1)}`}
              value={link.label}
              maxLength={80}
              style={{ flex: 1 }}
              onChange={(event) => {
                update(index, { label: event.currentTarget.value });
              }}
            />
            <TextInput
              label="Address"
              aria-label={`Address of ${name}`}
              placeholder="https://"
              value={link.href}
              style={{ flex: 2 }}
              onChange={(event) => {
                update(index, { href: event.currentTarget.value });
              }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              mb={4}
              aria-label={`Remove ${name}`}
              onClick={() => {
                onChange(value.filter((_, i) => i !== index));
              }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        );
      })}
      {error === undefined ? null : (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}
      <Group>
        <Button
          size="compact-sm"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={value.length >= 20}
          onClick={() => {
            onChange([...value, { label: "", href: "" }]);
          }}
        >
          Add a link
        </Button>
      </Group>
    </Stack>
  );
}

// --------------------------------------------------------------------------------------------------- series

function SeriesModal({
  series,
  onClose,
}: {
  readonly series: readonly SeriesRow[];
  readonly onClose: () => void;
}) {
  const save = useSaveSeries();
  const remove = useDeleteSeries();
  const assets = useAssets();
  const [editing, setEditing] = useState<SeriesRow | "new" | null>(
    series.length === 0 ? "new" : null,
  );
  const [deleting, setDeleting] = useState<SeriesRow | null>(null);
  const current = editing === "new" ? null : editing;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [artworkPath, setArtworkPath] = useState<string | null>(null);
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const start = (target: SeriesRow | "new") => {
    const row = target === "new" ? null : target;
    setTitle(row?.title ?? "");
    setDescription(row?.description ?? "");
    setArtworkPath(row?.artworkPath ?? null);
    setStartsOn(row?.startsOn ?? "");
    setEndsOn(row?.endsOn ?? "");
    setEditing(target);
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title="Series"
      size="lg"
      closeButtonProps={{ "aria-label": "Close" }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          A series groups sermons on one theme. Each series with a published sermon has its own page
          on your website, with its artwork and description.
        </Text>
        {editing === null ? (
          <>
            {series.length === 0 ? null : (
              <Table verticalSpacing="xs">
                <Table.Tbody>
                  {series.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {s.title}
                        </Text>
                        {s.startsOn === null ? null : (
                          <Text size="xs" c="dimmed">
                            {formatSermonDate(s.startsOn)}
                            {s.endsOn === null ? "" : ` to ${formatSermonDate(s.endsOn)}`}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            aria-label={`Edit ${s.title}`}
                            onClick={() => {
                              start(s);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            aria-label={`Delete ${s.title}`}
                            onClick={() => {
                              setDeleting(s);
                            }}
                          >
                            Delete
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <Group justify="space-between">
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  start("new");
                }}
              >
                New series
              </Button>
              <Button variant="subtle" onClick={onClose}>
                Done
              </Button>
            </Group>
          </>
        ) : (
          <>
            <TextInput
              label="Title"
              required
              maxLength={160}
              value={title}
              onChange={(event) => {
                setTitle(event.currentTarget.value);
              }}
            />
            <Textarea
              label="Description"
              autosize
              minRows={2}
              maxLength={2000}
              value={description}
              onChange={(event) => {
                setDescription(event.currentTarget.value);
              }}
            />
            <Select
              label="Artwork"
              description="From your images, with its description for people who cannot see it."
              placeholder="No artwork"
              clearable
              disabled={assets.isError}
              data={(assets.data ?? []).map((a) => ({ value: a.storage_path, label: a.alt_text }))}
              value={artworkPath}
              onChange={setArtworkPath}
            />
            <Group grow>
              <TextInput
                type="date"
                label="Starts"
                value={startsOn}
                onChange={(event) => {
                  setStartsOn(event.currentTarget.value);
                }}
              />
              <TextInput
                type="date"
                label="Ends"
                value={endsOn}
                error={
                  startsOn !== "" && endsOn !== "" && endsOn < startsOn
                    ? "Ends after it starts"
                    : undefined
                }
                onChange={(event) => {
                  setEndsOn(event.currentTarget.value);
                }}
              />
            </Group>
            <Group justify="flex-end">
              <Button
                variant="subtle"
                onClick={() => {
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button
                loading={save.isPending}
                disabled={
                  title.trim() === "" || (startsOn !== "" && endsOn !== "" && endsOn < startsOn)
                }
                onClick={() => {
                  const asset = (assets.data ?? []).find((a) => a.storage_path === artworkPath);
                  save.mutate(
                    {
                      seriesId: current?.id ?? null,
                      title,
                      description,
                      artworkPath,
                      artworkAlt: asset?.alt_text ?? current?.artworkAlt ?? null,
                      startsOn: startsOn === "" ? null : startsOn,
                      endsOn: endsOn === "" ? null : endsOn,
                    },
                    {
                      onSuccess: () => {
                        notifications.show({
                          color: "teal",
                          title: "Series saved",
                          message: title.trim(),
                        });
                        setEditing(null);
                      },
                      onError: (e) => {
                        notifyError("Not saved", e);
                      },
                    },
                  );
                }}
              >
                Save series
              </Button>
            </Group>
          </>
        )}
      </Stack>
      <ConfirmActionModal
        opened={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title="Delete this series"
        message={`Delete ${deleting?.title ?? ""}? Its sermons stay, in no series.`}
        confirmLabel="Delete series"
        danger
        loading={remove.isPending}
        onConfirm={async () => {
          if (deleting === null) return;
          try {
            await remove.mutateAsync(deleting.id);
          } catch (e) {
            notifyError("Not deleted", e);
          }
          setDeleting(null);
        }}
      />
    </Modal>
  );
}

// --------------------------------------------------------------------------------------------------- speakers

function SpeakersModal({
  speakers,
  onClose,
}: {
  readonly speakers: readonly SpeakerRow[];
  readonly onClose: () => void;
}) {
  const save = useSaveSpeaker();
  const remove = useDeleteSpeaker();
  const [name, setName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [deleting, setDeleting] = useState<SpeakerRow | null>(null);
  const saveSpeaker = (input: {
    speakerId: string | null;
    name: string;
    roleTitle: string;
    isActive: boolean;
  }) => {
    save.mutate(input, {
      onSuccess: () => {
        if (input.speakerId === null) {
          setName("");
          setRoleTitle("");
        }
      },
      onError: (e) => {
        notifyError("Not saved", e);
      },
    });
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title="Speakers"
      size="lg"
      closeButtonProps={{ "aria-label": "Close" }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          The names your website prints beside each sermon. A speaker you no longer use can be
          hidden from the list; their sermons keep their name.
        </Text>
        {speakers.length === 0 ? null : (
          <Table verticalSpacing="xs">
            <Table.Tbody>
              {speakers.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {s.name}
                    </Text>
                    {s.roleTitle === null ? null : (
                      <Text size="xs" c="dimmed">
                        {s.roleTitle}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      label="In the list"
                      aria-label={`${s.name} in the list`}
                      checked={s.isActive}
                      onChange={(event) => {
                        saveSpeaker({
                          speakerId: s.id,
                          name: s.name,
                          roleTitle: s.roleTitle ?? "",
                          isActive: event.currentTarget.checked,
                        });
                      }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      aria-label={`Delete ${s.name}`}
                      onClick={() => {
                        setDeleting(s);
                      }}
                    >
                      Delete
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        <Group align="flex-end" gap="xs">
          <TextInput
            label="Name"
            placeholder="Pastor Ada Obi"
            maxLength={120}
            value={name}
            style={{ flex: 1 }}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
          <TextInput
            label="Role"
            placeholder="Lead pastor"
            maxLength={120}
            value={roleTitle}
            style={{ flex: 1 }}
            onChange={(event) => {
              setRoleTitle(event.currentTarget.value);
            }}
          />
          <Button
            loading={save.isPending}
            disabled={name.trim() === ""}
            style={{ flexShrink: 0 }}
            onClick={() => {
              saveSpeaker({ speakerId: null, name, roleTitle, isActive: true });
            }}
          >
            Add speaker
          </Button>
        </Group>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Done
          </Button>
        </Group>
      </Stack>
      <ConfirmActionModal
        opened={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title="Delete this speaker"
        message={`Delete ${deleting?.name ?? ""}? Their sermons stay, with no speaker named. To keep the name on them, take the speaker out of the list instead.`}
        confirmLabel="Delete speaker"
        danger
        loading={remove.isPending}
        onConfirm={async () => {
          if (deleting === null) return;
          try {
            await remove.mutateAsync(deleting.id);
          } catch (e) {
            notifyError("Not deleted", e);
          }
          setDeleting(null);
        }}
      />
    </Modal>
  );
}

// --------------------------------------------------------------------------------------------------- podcast

function PodcastModal({
  feedUrl,
  onClose,
}: {
  readonly feedUrl: string | null;
  readonly onClose: () => void;
}) {
  const settings = useSermonSettings();
  return (
    <Modal
      opened
      onClose={onClose}
      title="Podcast"
      size="lg"
      closeButtonProps={{ "aria-label": "Close" }}
    >
      {settings.isPending ? (
        <LoadingState rows={3} />
      ) : settings.isError ? (
        <ErrorState message={settings.error.message} onRetry={() => void settings.refetch()} />
      ) : (
        <PodcastForm initial={settings.data} feedUrl={feedUrl} onClose={onClose} />
      )}
    </Modal>
  );
}

function PodcastForm({
  initial,
  feedUrl,
  onClose,
}: {
  readonly initial: SermonSettingsRow;
  readonly feedUrl: string | null;
  readonly onClose: () => void;
}) {
  const save = useSaveSermonSettings();
  const assets = useAssets();
  const [value, setValue] = useState<SermonSettingsRow>(initial);
  const set = (patch: Partial<SermonSettingsRow>) => {
    setValue((current) => ({ ...current, ...patch }));
  };
  const emailOk =
    value.email.trim() === "" || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.email.trim());
  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Every published sermon with audio becomes an episode. Submit the feed's address to Apple
        Podcasts and Spotify once; new sermons reach them on their own.
      </Text>
      <Switch
        label="Publish a podcast"
        checked={value.enabled}
        onChange={(event) => {
          set({ enabled: event.currentTarget.checked });
        }}
      />
      <TextInput
        label="Title"
        required={value.enabled}
        placeholder="Alpha Church sermons"
        maxLength={160}
        value={value.title}
        error={value.enabled && value.title.trim() === "" ? "A podcast needs a title" : undefined}
        onChange={(event) => {
          set({ title: event.currentTarget.value });
        }}
      />
      <Textarea
        label="Description"
        autosize
        minRows={2}
        maxLength={4000}
        value={value.description}
        onChange={(event) => {
          set({ description: event.currentTarget.value });
        }}
      />
      <Group grow align="flex-start">
        <TextInput
          label="Author"
          description="Shown as the podcast's author; usually the church."
          maxLength={160}
          value={value.author}
          onChange={(event) => {
            set({ author: event.currentTarget.value });
          }}
        />
        <TextInput
          label="Contact email"
          description="Apple asks for one to confirm the podcast is yours. Use the office's address: it is in the feed."
          value={value.email}
          error={emailOk ? undefined : "Check the email address"}
          onChange={(event) => {
            set({ email: event.currentTarget.value });
          }}
        />
      </Group>
      <Group grow align="flex-start">
        <Select
          label="Artwork"
          description="Square, 1400 to 3000 pixels, as Apple asks."
          placeholder="No artwork"
          clearable
          disabled={assets.isError}
          data={(assets.data ?? []).map((a) => ({ value: a.storage_path, label: a.alt_text }))}
          value={value.artworkPath}
          onChange={(artworkPath) => {
            set({ artworkPath });
          }}
        />
        <Select
          label="Category"
          allowDeselect={false}
          data={PODCAST_CATEGORIES.map((c) => ({ value: c, label: c }))}
          value={value.category}
          onChange={(category) => {
            if (category !== null) set({ category });
          }}
        />
      </Group>
      {feedUrl === null || !initial.enabled ? null : (
        <TextInput
          label="Feed address"
          readOnly
          value={feedUrl}
          description="Copy this into Apple Podcasts Connect and Spotify for Creators."
        />
      )}
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button
          loading={save.isPending}
          disabled={(value.enabled && value.title.trim() === "") || !emailOk}
          onClick={() => {
            save.mutate(value, {
              onSuccess: () => {
                notifications.show({
                  color: "teal",
                  title: value.enabled ? "Podcast saved" : "Podcast off",
                  message: value.enabled
                    ? "The feed is live at /sermons/feed.xml on your website."
                    : "Podcast apps stop receiving new episodes.",
                });
                onClose();
              },
              onError: (e) => {
                notifyError("Not saved", e);
              },
            });
          }}
        >
          Save podcast
        </Button>
      </Group>
    </Stack>
  );
}
